import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { endorsementCatalog } from '@/shared/api/resources';
import { Button } from '@/shared/ui/Button';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { statusLabel } from '@/shared/ui/statusLabels';

import {
  filtrosVazios,
  situacoesDaCotacao,
  tiposDaCotacao,
  tiposDeEndossoDoOnPoint,
  type QuoteFilters,
} from './cotacao';

/**
 * Dialogo "Cotacoes", com os filtros na ordem do OnPoint.
 *
 * Os filtros so valem ao aplicar: a lista consulta o servidor uma vez, com todos,
 * e nao a cada tecla. "Limpar filtro" volta tudo ao vazio. O tipo de endosso vem do
 * catalogo de endosso da empresa (P-END-1); sem a leitura do catalogo, o filtro fica
 * desligado, com o motivo.
 */
export function OperationFiltersDialog({
  open,
  value,
  onApply,
  onClose,
}: {
  open: boolean;
  value: QuoteFilters;
  onApply: (filtros: QuoteFilters) => void;
  onClose: () => void;
}) {
  const [rascunho, setRascunho] = useState<QuoteFilters>(value);
  const [aberto, setAberto] = useState(open);
  // Cada abertura parte do que esta aplicado, e nao do que ficou digitado na ultima.
  if (open !== aberto) {
    setAberto(open);
    if (open) setRascunho(value);
  }

  function campo<K extends keyof QuoteFilters>(chave: K, valor: QuoteFilters[K]) {
    setRascunho((atual) => ({ ...atual, [chave]: valor }));
  }

  const tiposDeEndosso = useQuery({
    queryKey: ['endorsement-types', 'todos'],
    queryFn: ({ signal }) => endorsementCatalog.types(false, signal),
    enabled: open,
    retry: false,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cotacoes"
      description="Filtros da lista de cotacoes."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={() => setRascunho(filtrosVazios)}>
            Limpar filtro
          </Button>
          <Button onClick={() => onApply(rascunho)}>Aplicar filtro</Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Numero da cotacao"
          placeholder="Digite"
          inputMode="numeric"
          value={rascunho.number}
          onChange={(event) => campo('number', event.target.value)}
        />
        <Select
          label="Status da cotacao"
          placeholder="Todos"
          value={rascunho.proposal_status}
          options={situacoesDaCotacao.map((situacao) => ({
            value: situacao,
            label: statusLabel('cotacao', situacao),
          }))}
          onChange={(event) => campo('proposal_status', event.target.value)}
        />
        <Field
          label="Data inicial"
          type="date"
          value={rascunho.created_from}
          onChange={(event) => campo('created_from', event.target.value)}
        />
        <Field
          label="Data final"
          type="date"
          value={rascunho.created_to}
          onChange={(event) => campo('created_to', event.target.value)}
        />
        <Select
          label="Tipo da cotacao"
          placeholder="Todos"
          value={rascunho.proposal_type}
          options={tiposDaCotacao.map((tipo) => ({
            value: tipo,
            label: statusLabel('tipoCotacao', tipo),
          }))}
          onChange={(event) => campo('proposal_type', event.target.value)}
        />
        <Select
          label="Tipo de endosso"
          placeholder="Todos"
          value={rascunho.endorsement_type_id}
          disabled={!tiposDeEndosso.isSuccess}
          hint={
            tiposDeEndosso.isError
              ? `Sem a leitura do catalogo de endosso. Opcoes padrao: ${tiposDeEndossoDoOnPoint.join(', ')}.`
              : 'Os tipos do catalogo de endosso da empresa.'
          }
          options={(tiposDeEndosso.data ?? []).map((tipo) => ({
            value: tipo.id,
            label: tipo.name,
          }))}
          onChange={(event) => campo('endorsement_type_id', event.target.value)}
        />
        <Field
          label="Nome do corretor"
          placeholder="Digite"
          value={rascunho.broker_name}
          onChange={(event) => campo('broker_name', event.target.value)}
        />
        <Field
          label="Nome do tomador"
          placeholder="Digite"
          value={rascunho.policyholder_name}
          onChange={(event) => campo('policyholder_name', event.target.value)}
        />
        <Field
          label="Documento do tomador"
          placeholder="Digite"
          value={rascunho.policyholder_document}
          onChange={(event) => campo('policyholder_document', event.target.value)}
        />
      </div>
      <div className="mt-4 flex flex-wrap gap-4">
        {(
          [
            ['big_risk', 'Grande risco'],
            ['monitoring', 'Monitoramento'],
            ['step_in', 'Retoma de objeto (Step-in)'],
          ] as const
        ).map(([chave, rotulo]) => (
          <label
            key={chave}
            className="flex cursor-pointer items-center gap-2 text-sm text-ink-700"
          >
            <input
              type="checkbox"
              checked={rascunho[chave]}
              onChange={(event) => campo(chave, event.target.checked)}
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            {rotulo}
          </label>
        ))}
      </div>
    </Modal>
  );
}
